import {
  Button,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Textarea,
  TextInput,
  VisuallyHidden,
} from '@mantine/core';
import { IconPlus, IconSchool } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import table from '../pms/Pms.module.css';
import work from '../pms/PmsWork.module.css';
import { usePmsQuery } from '../pms/PmsWorkParts.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import { drillRecordPath } from './DrillFindings.js';
import classes from './DrillPlanner.module.css';
import records from './DrillRecords.module.css';
import type {
  TrainingNeed,
  TrainingNeedSource,
  TrainingNeedStatus,
  TrainingNeeds,
} from './finding-types.js';

export interface TrainingNeedsScreenProps {
  source: TrainingNeedSource;
}

const TONE: Record<TrainingNeedStatus, StatusTone> = {
  open: 'warning',
  completed: 'success',
  cancelled: 'neutral',
};

/**
 * Crew ▸ Training & Courses (ADR 0128 Faz 4 Ek): tatbikat bulgusundan açılan ya da elle girilen
 * eğitim ihtiyaçları; her biri nasıl karşılandığıyla kapanır. Gemide yazılır, ofiste görülür.
 */
export function TrainingNeedsScreen({ source }: TrainingNeedsScreenProps) {
  const { t } = useTranslation();
  const [query, setQuery] = usePmsQuery();
  const vesselId = source.scope === 'office' ? query.get('vessel') : null;
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; data: TrainingNeeds }
  >({ kind: 'loading' });
  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', data: await source.load(vesselId) });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);
  const header = (
    <PageHeader
      title={t('menu.crewTraining')}
      description={t('drillFinding.training.description')}
    />
  );
  if (state.kind === 'loading') {
    return (
      <div className={table.screen}>
        {header}
        <TableSkeleton columns={6} label={t('menu.crewTraining')} />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className={table.screen}>
        {header}
        <Panel>
          <LoadError
            code={state.code}
            title={t('drillFinding.training.error')}
            onRetry={() => void load()}
          />
        </Panel>
      </div>
    );
  }
  return (
    <div className={table.screen}>
      {header}
      <TrainingNeedsBody
        data={state.data}
        source={source}
        reload={load}
        status={query.get('status') === 'all' ? 'all' : 'open'}
        selected={query.get('need')}
        onStatus={(value) => setQuery({ status: value === 'open' ? null : value })}
        onVessel={(id) => setQuery({ vessel: id })}
      />
    </div>
  );
}

type Dialog =
  | { kind: 'new' }
  | { kind: 'complete'; need: TrainingNeed }
  | { kind: 'cancel'; need: TrainingNeed }
  | null;

/** Yüklenmiş liste (ekran testleri doğrudan çizer). */
export function TrainingNeedsBody({
  data,
  source,
  reload,
  status,
  selected,
  onStatus,
  onVessel,
}: {
  data: TrainingNeeds;
  source: TrainingNeedSource;
  reload: () => Promise<void>;
  status: 'open' | 'all';
  selected: string | null;
  onStatus: (value: 'open' | 'all') => void;
  onVessel: (vesselId: string | null) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillFinding.training.${key}`, values);
  const c = (key: string) => k(`columns.${key}`);
  const shell = useShell();
  const [dialog, setDialog] = useState<Dialog>(null);
  const office = data.side === 'office';
  const ship = data.side === 'ship';
  // Seçili kayıt (bağlantıyla gelen) süzgeçten bağımsız görünür
  const needs = useMemo(
    () => data.needs.filter((n) => status === 'all' || n.status === 'open' || n.id === selected),
    [data.needs, status, selected],
  );
  const done = async () => {
    setDialog(null);
    await reload();
  };
  return (
    <>
      <Panel>
        <div className={classes.bar}>
          <SegmentedControl
            aria-label={c('status')}
            value={status}
            onChange={(value) => onStatus(value as 'open' | 'all')}
            data={[
              { value: 'open', label: k('filterOpen') },
              { value: 'all', label: k('filterAll') },
            ]}
          />
          {office && data.vessels.length > 1 ? (
            <Select
              aria-label={t('drillFinding.columns.vessel')}
              placeholder={t('drillRecord.list.allVessels')}
              clearable
              data={data.vessels.map((v) => ({ value: v.id, label: v.name }))}
              value={new URLSearchParams(shell?.search ?? '').get('vessel')}
              onChange={onVessel}
              maw={260}
            />
          ) : null}
          {ship && data.can.create && source.create ? (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setDialog({ kind: 'new' })}>
              {k('add')}
            </Button>
          ) : null}
        </div>
      </Panel>
      <Panel>
        {needs.length === 0 ? (
          <EmptyState icon={<IconSchool size={22} />} title={k('empty')} />
        ) : (
          <div className={table.tableBox}>
            <table className={table.table} data-cards data-testid="training-needs">
              <thead>
                <tr>
                  <th scope="col">{c('person')}</th>
                  {office ? <th scope="col">{t('drillFinding.columns.vessel')}</th> : null}
                  <th scope="col">{c('topic')}</th>
                  <th scope="col">{c('source')}</th>
                  <th scope="col">{c('due')}</th>
                  <th scope="col">{c('status')}</th>
                  <th scope="col">
                    <VisuallyHidden>{t('drillFinding.columns.actions')}</VisuallyHidden>
                  </th>
                </tr>
              </thead>
              <tbody>
                {needs.map((need) => (
                  <tr key={need.id} data-selected={need.id === selected || undefined}>
                    <td data-label={c('person')}>
                      <strong>{need.name}</strong>
                      {need.rank ? <div className={classes.muted}>{need.rank}</div> : null}
                    </td>
                    {office ? (
                      <td data-label={t('drillFinding.columns.vessel')}>
                        {need.vesselName ?? '—'}
                      </td>
                    ) : null}
                    <td data-label={c('topic')}>
                      {need.topic}
                      {need.reason ? <div className={classes.muted}>{need.reason}</div> : null}
                    </td>
                    <td data-label={c('source')}>
                      {need.drillSource ? (
                        <button
                          type="button"
                          className={classes.linkButton}
                          onClick={() =>
                            shell?.navigate(
                              drillRecordPath(
                                need.drillSource?.recordId as string,
                                office ? need.vesselId : null,
                              ),
                            )
                          }
                        >
                          {t('drillFinding.source', { no: need.drillSource.recordNo })}
                        </button>
                      ) : (
                        k('manual')
                      )}
                      {need.drillSource ? (
                        <div className={classes.muted}>
                          {t('drillFinding.sourceFinding', { no: need.drillSource.findingNo })}
                        </div>
                      ) : null}
                    </td>
                    <td data-label={c('due')}>{need.dueOn ? formatDay(need.dueOn) : '—'}</td>
                    <td data-label={c('status')}>
                      <span className={records.badges}>
                        <StatusBadge tone={TONE[need.status]}>
                          {k(`status.${need.status}`)}
                        </StatusBadge>
                        {need.overdue ? (
                          <StatusBadge tone="danger">{t('drillFinding.overdue')}</StatusBadge>
                        ) : null}
                      </span>
                      {/* Tamamlanma günü ve notu durumun altında (dar ekranda tablo taşmaz) */}
                      {need.completedOn ? (
                        <div className={classes.muted}>
                          {c('completed')}: {formatDay(need.completedOn)}
                        </div>
                      ) : null}
                      {need.completionNote ? (
                        <div className={classes.muted}>{need.completionNote}</div>
                      ) : null}
                      {need.cancelReason ? (
                        <div className={classes.muted}>{need.cancelReason}</div>
                      ) : null}
                    </td>
                    <td>
                      {ship && data.can.edit && need.status === 'open' ? (
                        <span className={records.rowActions} data-stack>
                          {source.complete ? (
                            <Button size="xs" onClick={() => setDialog({ kind: 'complete', need })}>
                              {k('complete')}
                            </Button>
                          ) : null}
                          {source.cancel ? (
                            <Button
                              size="xs"
                              variant="subtle"
                              color="red"
                              onClick={() => setDialog({ kind: 'cancel', need })}
                            >
                              {k('cancel')}
                            </Button>
                          ) : null}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {dialog ? (
        <NeedDialog
          dialog={dialog}
          data={data}
          source={source}
          onClose={() => setDialog(null)}
          onSaved={done}
        />
      ) : null}
    </>
  );
}

function NeedDialog({
  dialog,
  data,
  source,
  onClose,
  onSaved,
}: {
  dialog: Exclude<Dialog, null>;
  data: TrainingNeeds;
  source: TrainingNeedSource;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const f = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillFinding.training.form.${key}`, values);
  const today = data.vessels[0]?.today ?? null;
  const [person, setPerson] = useState<string | null>(null);
  const [topic, setTopic] = useState('');
  const [reason, setReason] = useState('');
  const [day, setDay] = useState<string | null>(dialog.kind === 'complete' ? today : null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const required = t('drillPlan.fieldErrors.REQUIRED');
  const title =
    dialog.kind === 'new'
      ? f('title')
      : dialog.kind === 'complete'
        ? f('completeTitle', { topic: dialog.need.topic })
        : f('cancelTitle', { topic: dialog.need.topic });
  const save = async () => {
    const empty =
      dialog.kind === 'new'
        ? !person || topic.trim() === ''
        : dialog.kind === 'complete'
          ? !day || reason.trim() === ''
          : reason.trim() === '';
    if (empty) {
      setMissing(true);
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      if (dialog.kind === 'new') {
        await source.create?.({
          userId: person as string,
          topic: topic.trim(),
          reason: reason.trim() === '' ? null : reason.trim(),
          dueOn: day,
        });
      } else if (dialog.kind === 'complete') {
        await source.complete?.({
          id: dialog.need.id,
          rowVersion: dialog.need.rowVersion,
          need: { completedOn: day as string, note: reason.trim() },
        });
      } else {
        await source.cancel?.({
          id: dialog.need.id,
          rowVersion: dialog.need.rowVersion,
          reason: reason.trim(),
        });
      }
      await onSaved();
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  };
  const code = failure ? errorCode(failure) : null;
  return (
    <Modal
      opened
      onClose={onClose}
      title={title}
      closeButtonProps={{ 'aria-label': t('drillFinding.form.close') }}
    >
      <Stack gap="sm">
        {dialog.kind === 'new' ? (
          <>
            <Select
              label={f('person')}
              required
              searchable
              data={data.crew.map((p) => ({
                value: p.userId,
                label: p.rank ? `${p.name} · ${p.rank}` : p.name,
              }))}
              value={person}
              onChange={setPerson}
              error={missing && !person ? required : undefined}
            />
            <TextInput
              label={f('topic')}
              required
              maxLength={200}
              value={topic}
              onChange={(event) => setTopic(event.currentTarget.value)}
              error={missing && topic.trim() === '' ? required : undefined}
            />
          </>
        ) : null}
        {dialog.kind !== 'cancel' ? (
          <DayInput
            label={dialog.kind === 'new' ? f('due') : f('completedOn')}
            required={dialog.kind === 'complete'}
            maxDate={dialog.kind === 'complete' && today ? today : undefined}
            value={day}
            onChange={(value) => setDay(value ?? null)}
            error={missing && dialog.kind === 'complete' && !day ? required : undefined}
          />
        ) : null}
        <Textarea
          label={
            dialog.kind === 'new'
              ? f('reason')
              : dialog.kind === 'complete'
                ? f('note')
                : f('cancelReason')
          }
          required={dialog.kind !== 'new'}
          autosize
          minRows={2}
          maxLength={2000}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
          error={missing && dialog.kind !== 'new' && reason.trim() === '' ? required : undefined}
        />
        {code ? (
          <p className={work.error} role="alert">
            {t(`drillFinding.errors.${code}`, {
              defaultValue: t(`drillPlan.errors.${code}`, {
                defaultValue: t('drillPlan.errors.GENERIC'),
              }),
            })}
          </p>
        ) : null}
        <div className={records.footerGroup}>
          <Button
            loading={busy}
            {...(dialog.kind === 'cancel' ? { color: 'red' } : {})}
            onClick={() => void save()}
          >
            {dialog.kind === 'cancel' ? t('drillFinding.training.cancel') : f('save')}
          </Button>
          <Button variant="default" onClick={onClose}>
            {t('drillFinding.form.close')}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}
